import { CSSProperties } from 'react';

export type WeekdaySelectorOption = {
    value: string;
    label: string;
};

const WEEKDAYS = [
    { name: 'Monday', initial: 'M' },
    { name: 'Tuesday', initial: 'T' },
    { name: 'Wednesday', initial: 'W' },
    { name: 'Thursday', initial: 'Th' },
    { name: 'Friday', initial: 'F' },
    { name: 'Saturday', initial: 'Sa' },
    { name: 'Sunday', initial: 'Su' },
] as const;

/** Stored values for pickup config and Uber markets: M, T, W, Th, F, Sa, Su. */
export const WEEKDAY_SELECTOR_INITIALS: WeekdaySelectorOption[] = WEEKDAYS.map((day) => ({
    value: day.initial,
    label: day.initial,
}));

export const weekdayShortLabel = (day: string) =>
    WEEKDAYS.find((weekday) => weekday.name === day || weekday.initial === day)?.initial ?? day;

const weekdayFullName = (day: string) =>
    WEEKDAYS.find((weekday) => weekday.initial === day || weekday.name === day)?.name ?? day;

type Props = {
    options?: WeekdaySelectorOption[];
    selected: string[];
    onToggle: (value: string) => void;
    disabled?: boolean;
    'aria-label'?: string;
};

const containerStyle: CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    maxWidth: 480,
};

const dayBaseStyle: CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 48,
    height: 48,
    borderRadius: '50%',
    fontSize: 16,
    cursor: 'pointer',
    transition: 'background-color 0.3s, color 0.3s',
};

const dayIdleStyle: CSSProperties = {
    ...dayBaseStyle,
    border: '2px solid #000',
    backgroundColor: '#fff',
    color: '#000',
};

const dayActiveStyle: CSSProperties = {
    ...dayBaseStyle,
    border: 'none',
    backgroundColor: '#007bff',
    color: '#fff',
};

const WeekdaySelector = ({
    options = WEEKDAY_SELECTOR_INITIALS,
    selected,
    onToggle,
    disabled = false,
    'aria-label': ariaLabel = 'Days of week',
}: Props) => {
    return (
        <div style={containerStyle} role="group" aria-label={ariaLabel}>
            {options.map((day, index) => {
                const isSelected = selected.includes(day.value);
                return (
                    <div
                        key={day.value}
                        role="button"
                        tabIndex={disabled ? -1 : 0}
                        title={weekdayFullName(day.value)}
                        aria-label={weekdayFullName(day.value)}
                        aria-pressed={isSelected}
                        onClick={() => {
                            if (!disabled) {
                                onToggle(day.value);
                            }
                        }}
                        onKeyDown={(event) => {
                            if (disabled) {
                                return;
                            }
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                onToggle(day.value);
                            }
                        }}
                        style={{
                            ...(isSelected ? dayActiveStyle : dayIdleStyle),
                            marginRight: index === options.length - 1 ? 0 : 8,
                            pointerEvents: disabled ? 'none' : 'auto',
                            opacity: disabled ? 0.5 : 1,
                        }}>
                        {day.label}
                    </div>
                );
            })}
        </div>
    );
};

export default WeekdaySelector;
